import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DailyUtilization } from '@/features/calendar/calendarUtilization';

/**
 * One-line daily utilization: only the percentage is shown, hours live in the
 * tooltip. A day without capacity renders nothing — there is no ratio to show.
 */
export function UtilizationSummary({ day, unavailable = false }: { day: DailyUtilization; unavailable?: boolean }) {
  const { t, i18n } = useTranslation('calendar');
  if (!unavailable && day.percent === null) return null;
  const hours = (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  const overloaded = day.occupiedHours > day.availableHours;
  const label = unavailable || day.percent === null ? t('utilization.unavailable') : `${String(day.percent)} %`;
  return (
    <Tooltip title={unavailable ? label : t('utilization.details', {
      occupied: hours(day.occupiedHours), available: hours(day.availableHours),
      unassigned: hours(day.unassignedHours),
    })}>
      <Box tabIndex={0} aria-label={`${t('utilization.title')} ${day.date}: ${label}`}
        sx={{ px: 0.5, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', bgcolor: 'action.hover',
          color: overloaded && !unavailable ? 'error.main' : 'text.primary' }}>
        <Typography variant="caption" fontWeight={700} noWrap>{label}</Typography>
      </Box>
    </Tooltip>
  );
}
